import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldArray } from 'formik';

import CustomSelect from '../../CustomSelect';
import CustomFormControl from '../../CustomFormControl';
import SensorSelector from '../ChartSelector/SensorSelector';
import CustomLabel from '../../CustomLabel';
import ColorMenu from '../../../../DigitalSensorsForm/ColorMenu';
import ErrorFeedbackP from '../../ErrorFeedbackP';
import { snapshotColorOptions, widgetIcons } from '../Snapshot';
import { useGetAll } from '../../../../../hooks/useGetAll';

const SnapshotSelector = ({
  handleBlur,
  handleChange,
  setFieldValue,
  setFieldTouched,
  values,
  errors,
  touched,
  submitForm,
  location,
  hasExclusiveActuator,
  hasExclusivePart,
  hasExclusiveSensor,
  valuesArray,
  setHasExclusive,
  validateForm,
}) => {
  const {
    data: { asset: assets },
  } = useGetAll('asset', { filter: { location } });
  const { t } = useTranslation();

  const hasAnyExclusive =
    hasExclusiveActuator || hasExclusivePart || hasExclusiveSensor;
  const shouldShowForm =
    valuesArray.length > 0 && !hasExclusivePart && !hasExclusiveActuator;
  const shouldShowIconSelect = !hasExclusiveSensor;
  const colorGridSize = hasExclusiveSensor ? 12 : 6;
  const measuringModeFilter =
    values.items.length > 1 ? ['streaming', 'positioning'] : null;

  return (
    <form onSubmit={submitForm}>
      <Grid container spacing={2} sx={{ width: '500px' }}>
        <Grid item xs={12}>
          <Alert severity="info" variant="filled" sx={{ width: 'fit-content' }}>
            {t('atoms.widget.selector.snapshot_info')}
          </Alert>
        </Grid>

        <Grid item xs={12}>
          <FieldArray
            name="items"
            render={arrayHelpers =>
              values.items?.map((item, index) => (
                <SensorSelector
                  key={item.id}
                  assets={assets}
                  values={values}
                  arrayHelpers={arrayHelpers}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  isList
                  index={index}
                  measuringModeFilter={measuringModeFilter}
                  hasExclusive={hasAnyExclusive}
                  setHasExclusive={setHasExclusive}
                  setFieldValue={setFieldValue}
                  validateForm={validateForm}
                />
              ))
            }
          />
        </Grid>

        {shouldShowForm && (
          <>
            <Grid item xs={12}>
              <CustomFormControl
                id="title"
                label={t('atoms.widget.selector.title')}
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                touched={touched}
                required
              />
            </Grid>

            {shouldShowIconSelect && (
              <Grid item xs={6}>
                <CustomSelect
                  id="icon"
                  label={t('atoms.widget.selector.icon')}
                  options={Object.keys(widgetIcons).map(key => ({
                    value: key,
                    label: t(`atoms.widget.selector.options_snapshot.${key}`),
                  }))}
                  value={values.icon}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                />
              </Grid>
            )}

            <Grid
              item
              xs={colorGridSize}
              justifyContent="flex-start"
              alignItems="flex-start"
              display="flex"
              flexDirection="column"
            >
              <CustomLabel required htmlFor="color">
                {t('components.digital_sensors_form.color.label')}
              </CustomLabel>
              <ColorMenu
                id="color"
                selectedColor={values.color}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                touched={touched}
                fullWidth
                colorOptions={snapshotColorOptions}
              />
              <ErrorFeedbackP error={errors.color} touched={touched.color} />
            </Grid>
          </>
        )}
      </Grid>
    </form>
  );
};

SnapshotSelector.propTypes = {
  errors: PropTypes.any.isRequired,
  handleBlur: PropTypes.any.isRequired,
  valuesArray: PropTypes.any.isRequired,
  hasExclusiveSensor: PropTypes.any.isRequired,
  hasExclusivePart: PropTypes.any.isRequired,
  hasExclusiveActuator: PropTypes.any.isRequired,
  setHasExclusive: PropTypes.any.isRequired,
  handleChange: PropTypes.any.isRequired,
  setFieldValue: PropTypes.any.isRequired,
  setFieldTouched: PropTypes.any.isRequired,
  location: PropTypes.string.isRequired,
  submitForm: PropTypes.any.isRequired,
  touched: PropTypes.any.isRequired,
  validateForm: PropTypes.any.isRequired,
  values: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
    color: PropTypes.string,
    items: PropTypes.array,
  }).isRequired,
};

export default SnapshotSelector;
